import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback
} from "react"

import detectEthereumProvider from "@metamask/detect-provider"

// One Wei
const WEI_UNIT = 1000000000000000000;

/**
 * Network list
 * Consider moving to a place where it can be periodically updated.
 */
const NETWORKS = [
  {
    chainId: '0x0',
    numericChainId: 0,
    name: 'Unknown',
    currency: '',
    ehterscanAddress: ''
  },
  {
    chainId: '0xaa36a7',
    numericChainId: 11155111,
    name: 'Sepholia',
    currency: 'SepholiaETH',
    ehterscanAddress: 'https://sepolia.etherscan.io/address/'
  },
  {
    chainId: '0x1',
    numericChainId: 1,
    name: 'Mainnet',
    currency: 'ETH',
    ehterscanAddress: 'https://etherscan.io/address/'
  },
]

const disconnectedState = { accounts: [], balance: "", chainId: "" }

const MetaMaskContext = createContext({})

export const MetaMaskContextProvider = ({ children }) => {
  const [hasProvider, setHasProvider] = useState(null)

  const [isConnecting, setIsConnecting] = useState(false)

  const [errorMessage, setErrorMessage] = useState("")

  const [wallet, setWallet] = useState(disconnectedState)

  const clearError = () => setErrorMessage("")

  const _updateWallet = useCallback(async providedAccounts => {
    const accounts =
      providedAccounts ||
      (await window.ethereum.request({ method: "eth_accounts" }))

    if (accounts.length === 0) {
      // If there are no accounts, then the user is disconnected
      setWallet(disconnectedState)
      return
    }

    const balance = formatBalance(
      await window.ethereum.request({
        method: "eth_getBalance",
        params: [accounts[0], "latest"]
      })
    )
    const chainId = await window.ethereum.request({
      method: "eth_chainId"
    })

    const network = getNetworkInfo(chainId);

    setWallet({ accounts, balance, chainId, network })
  }, [])

  const updateWalletAndAccounts = useCallback(() => _updateWallet(), [
    _updateWallet
  ])
  const updateWallet = useCallback(accounts => _updateWallet(accounts), [
    _updateWallet
  ])

  useEffect(() => {
    const getProvider = async () => {
      const provider = await detectEthereumProvider({ silent: true })
      setHasProvider(Boolean(provider))

      if (provider) {
        updateWalletAndAccounts()
        window.ethereum.on("accountsChanged", updateWallet)
        window.ethereum.on("chainChanged", updateWalletAndAccounts)
      }
    }

    getProvider()

    return () => {
      window.ethereum?.removeListener("accountsChanged", updateWallet)
      window.ethereum?.removeListener("chainChanged", updateWalletAndAccounts)
    }
  }, [updateWallet, updateWalletAndAccounts])

  const connectMetaMask = async () => {
    setIsConnecting(true)

    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts"
      })
      clearError()
      updateWallet(accounts)
    } catch (err) {
      setErrorMessage(err.message)
    }
    setIsConnecting(false)
  }

  const formatBalance = (rawBalance) => {
    const balance = (parseInt(rawBalance) / WEI_UNIT).toFixed(4)
    return balance
  }

  const getNetworkInfo = (chainId) => {
    let networks = NETWORKS.filter(function (network) {
      return network.chainId === chainId
    });

    return networks.length > 0 ? networks[0] : NETWORKS[0];
  }

  return (
    <MetaMaskContext.Provider
      value={{
        wallet,
        hasProvider,
        error: !!errorMessage,
        errorMessage,
        isConnecting,
        connectMetaMask,
        clearError
      }}
    >
      {children}
    </MetaMaskContext.Provider>
  )
}

export const useMetaMask = () => {
  const context = useContext(MetaMaskContext)
  if (context === undefined) {
    throw new Error(
      'useMetaMask must be used within a "MetaMaskContextProvider"'
    )
  }
  return context
}
